import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import isNil from 'lodash/isNil';
import {
  useFormContext,
} from 'react-hook-form';
import Controller from 'core/components/FormController';
import Accordion from 'core/components/Accordion';
import Select from 'core/components/Select';
import ServiceFacilitySelect from 'pages/Dashboard/components/ServiceFacilitySelect';
import {
  useGetBillingProviders,
  useGetUsers,
} from 'pages/Dashboard/hooks/providers';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import {
  ClaimDataForm,
} from 'pages/Dashboard/pages/Billing/types';
import ReferringProvider from 'pages/Dashboard/components/ReferringProvider';
import NPIInfo from 'pages/Dashboard/components/NPIInfo';
import TaxIdInfo from 'pages/Dashboard/components/TaxIdInfo';
import {
  BillingProviderDTO,
  NPISearchItemDTO,
  PatientDTO,
  ProviderEntryDTO,
  RenderingProviderDTO,
  ServiceFacilityDTO,
  UserDTO,
} from 'dtos';
import {
  claimEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  getAddressString,
} from 'utils/string';
import {
  isNilOrZero,
} from '@populateui/utils';
import Checkbox from 'pages/Dashboard/components/Input/Checkbox';
import clsx from 'clsx';
import mapNPISearchItemToProviderEntry from 'pages/Dashboard/utils/providers';
import {
  useClaimReferringProviderSuggestions,
} from 'pages/Dashboard/components/ReferringProvider/hooks';
import isEqual from 'lodash/isEqual';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';

type Props = {
  patient?: PatientDTO;
};

export function BillingProviderLabel({
  billingProviderId,
  address,
  formattedNames,
  npi,
  taxId,
}: BillingProviderDTO) {
  const addressString = getAddressString(address);
  return (
    <div className="flex justify-center flex-col w-full">
      <span className="font-semibold w-full truncate uppercase">
        {getDisplayName(formattedNames)}
      </span>
      {!isNilOrZero(billingProviderId) && (
        <div>
          <small>{addressString}</small>
          <NPIInfo {...(npi ?? {})} />
          <TaxIdInfo {...(taxId ?? {})} />
        </div>
      )}
    </div>
  );
}

const providerClasses = {
  valueContainer: '!text-left !items-start flex-col !gap-0',
  name: 'font-semibold',
  button: '!h-12 flex-1',
  npiInfo: 'font-semibold text-[10px]',
};

export function UserLabel({
  formattedNames,
  npi,
  renderingProviderId,
}: UserDTO & RenderingProviderDTO) {
  return (
    <div className="flex justify-center flex-col w-full">
      <span className={clsx('font-semibold w-full truncate', {
        uppercase: renderingProviderId !== 0,
      })}
      >
        {getDisplayName(formattedNames, '—')}
      </span>
      <NPIInfo {...(npi ?? {})} />
    </div>
  );
}

export default function ClaimInfo({ patient }: Props) {
  const { control, setValue, getValues, watch } = useFormContext<ClaimDataForm>();
  const [
    referringProviderAsSupervisingProvider,
    setReferringProviderAsSupervisingProvider,
  ] = useState<boolean>(false);

  const { data: doctors = [] } = useGetUsers({
    onlyDoctors: true,
    sortBySelf: false,
  });

  const { data: billingProviders = [] } = useGetBillingProviders();
  const billingProvidersWithNotSet = useMemo(() => (
    [{ billingProviderId: 0, formattedNames: { indexByLastNameWithCredentials: 'Not Set' } }, ...billingProviders]
  ), [billingProviders]);
  const {
    suggestions: providerSuggestionData,
  } = useClaimReferringProviderSuggestions(
    watch('claimId'),
  );

  const [referringProvider, supervisingProvider] = watch(['referringProvider', 'supervisingProvider']);

  const isSameProvider = useMemo(() => (
    !isNil(referringProvider)
    && !isNil(supervisingProvider)
    && isEqual(referringProvider, supervisingProvider)
  ), [referringProvider, supervisingProvider]);

  const referringProviderSuggestions = useMemo(() => (
    providerSuggestionData.map((suggestion) => ({
      ...suggestion,
      onSelect: () => {
        setValue('referringProvider', suggestion.providerEntry);
        claimEventChannel.emit('onShouldUpdateBillingProvider');
      },
    }))
  ), [providerSuggestionData, setValue]);

  useEffect(() => {
    setReferringProviderAsSupervisingProvider(isSameProvider);
  }, [isSameProvider]);

  const onChangeServiceFacility = useCallback((serviceFacility: ServiceFacilityDTO) => {
    setValue('serviceFacility', serviceFacility);
    const [claimId, referencedPrimaryClaimId, billingCodes] = getValues(['claimId', 'referencedPrimaryClaimId', 'billingCodes']);

    const { defaultPOS } = serviceFacility;
    if (!isNil(defaultPOS)) {
      const claimIdKey = !isNil(referencedPrimaryClaimId) ? 'secondaryClaimId' : 'primaryClaimId';
      setValue('billingCodes', (billingCodes ?? []).map((serviceCode) => ({
        ...serviceCode,
        ...(serviceCode?.serviceCodeDetails?.[claimIdKey] === claimId
          ? {
            serviceCodeDetails: {
              ...(serviceCode?.serviceCodeDetails ?? {}),
              pos: defaultPOS,
            },
          }
          : {}),
      })));
    }
    claimEventChannel.emit('onShouldUpdateBillingProvider');
  }, [getValues, setValue]);

  return (
    <Accordion
      defaultExpanded
      header="Facility & Provider"
      disableGutters
      headerClasses={{
        root: '!px-2 !py-1 !min-h-[initial] !bg-gray-100 !text-gray-600 !font-semibold text-sm',
        content: '!m-0',
      }}
      detailsClasses={{ root: '!p-2' }}
    >
      <div className="populate-form flex flex-col gap-4">
        <div className="flex gap-3">
          <Controller
            control={control}
            name="referringProvider"
            render={({ field }) => (
              <Wrapper
                id="referring-provider"
                className="w-[calc(33.33%-8px)] min-w-fit"
                label="Referring Provider (Box 17)"
              >
                <ReferringProvider
                  {...field}
                  value={field.value}
                  onChange={(value?: NPISearchItemDTO | null) => {
                    field.onChange(mapNPISearchItemToProviderEntry(value));
                  }}
                  hideBackdrop={false}
                  classes={providerClasses}
                  suggestions={referringProviderSuggestions}
                />
              </Wrapper>
            )}
          />
          <Controller
            control={control}
            name="supervisingProvider"
            render={({ field: { onChange, ...field } }) => (
              <Wrapper
                id="supervising-provider"
                className="w-[calc(33.33%-8px)] min-w-fit"
                label="Supervising Provider (Box 17)"
                containerClassName="!flex-col !gap-1 !items-start !text-xs"
              >
                <ReferringProvider
                  {...field}
                  value={field.value as ProviderEntryDTO}
                  hideBackdrop={false}
                  text="Supervising Provider"
                  classes={providerClasses}
                  onChange={(value?: NPISearchItemDTO | null) => {
                    onChange(mapNPISearchItemToProviderEntry(value));
                  }}
                  action={isNil(referringProvider) ? null : (
                    <Checkbox
                      label="Same as Referring Provider"
                      id="same-as-referring-provider"
                      checked={referringProviderAsSupervisingProvider}
                      onChange={(checked) => {
                        if (checked) {
                          onChange(referringProvider);
                        } else {
                          onChange(null);
                        }
                      }}
                    />
                  )}
                />
              </Wrapper>
            )}
          />
        </div>
        <Controller
          control={control}
          name="renderingProvider"
          render={({ field }) => (
            <Select<UserDTO>
              {...field}
              onBlur={field.onBlur}
              onChange={(value) => {
                field.onChange(value);
                claimEventChannel.emit('onShouldUpdateBillingProvider');
              }}
              id="rendering-provider"
              className="w-[calc(33.33%-8px)] text-gray-500 text-xs"
              label="Rendering Provider (Box 24j)"
              placeholder="Select"
              options={doctors ?? []}
              getLabel={UserLabel}
              getValue={(option) => option?.userId ?? 0}
            />
          )}
        />
        <div className="flex gap-3">
          <Controller
            control={control}
            name="serviceFacility"
            render={({ field }) => (
              <ServiceFacilitySelect
                {...field}
                onChange={onChangeServiceFacility}
                label="Service Facility (Box 32)"
                className="!w-[calc(33.33%-8px)]"
                additionalOptions={[{
                  serviceFacilityId: 0,
                  nickname: 'Patient\'s Address',
                  address: patient?.address,
                  defaultPOS: '12',
                }]}
                includeClaimsInfo
              />
            )}
          />
          <Controller
            control={control}
            name="billingProvider"
            render={({ field }) => (
              <Select<BillingProviderDTO>
                {...field}
                onChange={field.onChange}
                onBlur={field.onBlur}
                id="billing-provider"
                className="!w-[calc(33.33%-8px)] text-gray-500 text-xs min"
                selectClassName="!min-h-[64px]"
                label="Billing Provider (Box 33)"
                placeholder="Select"
                options={billingProvidersWithNotSet ?? []}
                getLabel={BillingProviderLabel}
                getValue={(option) => option?.billingProviderId ?? ''}
              />
            )}
          />
        </div>
      </div>
    </Accordion>
  );
}
